import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import MenuBar from "../../components/MenuBar";
import HeaderBar from "../../components/HeaderBar";
import { useAuth } from "../../context/AuthContext";
import { daysOutstandingForBill, fetchAllCreditBills, creditBillBranch } from "../../services/creditDueService";
import { useCreditBranchFilter } from "./useCreditBranchFilter";
import CreditBranchSelector from "./CreditBranchSelector";

function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

export default function CreditDueOver15Page() {
  const { user: storedUser } = useAuth();
  const { branch, setBranch, visibleBranches } = useCreditBranchFilter(storedUser);

  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const rows = await fetchAllCreditBills();
      setBills(rows);
    } catch (e) {
      setErr(e?.message || "Failed to load credit bills.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const billsBranch = useMemo(
    () => bills.filter((b) => creditBillBranch(b) === branch),
    [bills, branch]
  );

  const rows = useMemo(() => {
    const open = billsBranch.filter((b) => round2(b.balanceAmount) > 0);
    const map = new Map();
    for (const b of open) {
      const days = daysOutstandingForBill(b);
      if (days <= 15) continue;
      const cid = b.customerId;
      if (!cid) continue;
      if (!map.has(cid)) {
        map.set(cid, {
          customerId: cid,
          customerName: b.customerName || "",
          phone: b.phone || "",
          dueOver15: 0,
          billsOver15: 0,
          oldestDays: days,
        });
      }
      const r = map.get(cid);
      r.dueOver15 = round2(r.dueOver15 + round2(b.balanceAmount));
      r.billsOver15 += 1;
      r.oldestDays = Math.max(r.oldestDays, days);
    }
    return Array.from(map.values()).sort((a, b) => b.dueOver15 - a.dueOver15);
  }, [billsBranch]);

  const totals = useMemo(() => {
    return {
      customers: rows.length,
      amount: round2(rows.reduce((s, r) => s + (Number(r.dueOver15) || 0), 0)),
      bills: rows.reduce((s, r) => s + (Number(r.billsOver15) || 0), 0),
    };
  }, [rows]);

  return (
    <div className="min-h-screen bg-slate-100/90">
      <MenuBar />
      <HeaderBar />
      <main className="mx-auto max-w-6xl px-3 pb-24 pt-4 sm:px-4">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <Link to="/credit-due" className="text-sm font-semibold text-indigo-600 hover:underline">
              ← Back to Credit / Due
            </Link>
            <h1 className="mt-2 text-lg font-extrabold text-slate-900">Due &gt; 15 days</h1>
            <p className="text-xs text-slate-600">Amount includes only bill balances older than 15 days.</p>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <CreditBranchSelector value={branch} onChange={setBranch} options={visibleBranches} />
            <button
              type="button"
              onClick={load}
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-50"
            >
              Refresh
            </button>
          </div>
        </div>

        {err ? (
          <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{err}</div>
        ) : null}

        <div className="mb-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            <div>
              <p className="text-[11px] font-bold uppercase text-amber-700">Customers</p>
              <p className="mt-1 text-base font-extrabold text-amber-900">{totals.customers}</p>
            </div>
            <div>
              <p className="text-[11px] font-bold uppercase text-amber-700">Bills</p>
              <p className="mt-1 text-base font-extrabold text-amber-900">{totals.bills}</p>
            </div>
            <div>
              <p className="text-[11px] font-bold uppercase text-amber-700">Total amount</p>
              <p className="mt-1 text-base font-extrabold text-amber-900">₹{totals.amount.toLocaleString("en-IN")}</p>
            </div>
          </div>
        </div>

        {loading ? (
          <p className="text-sm text-slate-600">Loading…</p>
        ) : rows.length === 0 ? (
          <div className="rounded-xl border border-slate-200 bg-white p-6 text-center text-sm text-slate-600 shadow-sm">
            No balances older than 15 days.
          </div>
        ) : (
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 z-10 bg-slate-50 text-left text-xs font-bold uppercase text-slate-600">
                <tr>
                  <th className="p-3">Customer</th>
                  <th className="p-3">Phone</th>
                  <th className="p-3 text-right">Bills (&gt;15d)</th>
                  <th className="p-3 text-right">Oldest (days)</th>
                  <th className="p-3 text-right">Amount (&gt;15d)</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.customerId} className="border-t border-slate-100 hover:bg-slate-50/70">
                    <td className="p-3 font-semibold text-slate-900">
                      <Link to={`/credit-due/customer/${r.customerId}`} className="text-indigo-700 hover:underline">
                        {r.customerName || "—"}
                      </Link>
                    </td>
                    <td className="p-3 text-slate-600">{r.phone || "—"}</td>
                    <td className="p-3 text-right tabular-nums">{r.billsOver15}</td>
                    <td className="p-3 text-right tabular-nums">{r.oldestDays}</td>
                    <td className="p-3 text-right font-extrabold tabular-nums text-amber-900">
                      ₹{round2(r.dueOver15).toLocaleString("en-IN")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}

